import React from 'react'

import {
  AlignItems,
  Body2,
  Body3,
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontFamily,
  Heading4,
  Icon,
  IconVariant,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import type {
  SubscriptionOption,
  SubscriptionSettingsAnalytics,
} from '@library/api-customer-engagement-experience'

import { FormattedText } from '../helpers/FormattedText'

const OptionCardSurface = withCitrusTheme(Box, () => ({
  borderRadius: '8px',
}))

const OptionBadge = withCitrusTheme(Box, () => ({
  borderRadius: '4px',
}))

const OptionCTAButton = withCitrusTheme(Button, (theme) => [
  {
    padding: `${theme.spacing(3)}px ${theme.spacing(4)}px`,
    borderRadius: '8px',
  },
  theme.responsiveFont([4, 4, 4, 4]),
])

export const OptionCard = ({
  option,
  onAction,
}: {
  option: SubscriptionOption
  onAction: (intent: string, analytics?: SubscriptionSettingsAnalytics) => void
}) => {
  const isPrimary = option.id === 'resubscribe'

  return (
    <OptionCardSurface
      data-testid={`option-card-${option.id}`}
      position={DisplayPosition.Relative}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={4}
      bg={Color.White}
      paddingV={4}
      paddingH={4}
      boxShadow="0 3px 10px rgba(0, 0, 0, 0.10)"
      width={['100%', '50%', '50%', '50%']}
    >
      {option.badge && (
        <Box position={DisplayPosition.Absolute} top="16px" right="16px">
          <OptionBadge bg={Color.Success_50} color={Color.Success_900} paddingH={2} paddingV={0.5}>
            <Text size={[0, null, 4, 4]} fontFamily={FontFamily.Bold}>
              {option.badge}
            </Text>
          </OptionBadge>
        </Box>
      )}
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={3} flexGrow={1}>
        <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={1}>
          <Heading4 size={[7, null, 9, 9]} color={Color.ColdGrey_900}>
            {option.title}
          </Heading4>
          <Body2 size={[4, null, 4, 4]} color={Color.ColdGrey_700}>
            <FormattedText text={option.summary} />
          </Body2>
        </Box>
        <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={1}>
          {option.tags.map((tag) => (
            <Box key={tag} display={Display.Flex} alignItems={AlignItems.Center} gap={1}>
              <Icon name="tick" size={4} variant={IconVariant.Confirmation} />
              <Body3 size={[0, null, 2, 2]} color={Color.ColdGrey_800}>
                <FormattedText text={tag} />
              </Body3>
            </Box>
          ))}
        </Box>
      </Box>
      <OptionCTAButton
        data-testid={`option-cta-${option.id}`}
        colorVariant={isPrimary ? ButtonColorVariant.Primary : ButtonColorVariant.Secondary}
        onClick={() => onAction(option.action.intent, option.action.analytics)}
        width="100%"
      >
        {option.action.title}
      </OptionCTAButton>
    </OptionCardSurface>
  )
}
